import React, { useEffect, useState } from 'react';
import { Button, Dialog, IconButton } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import BrandMark from './brand/BrandMark';
import './ProductTour.css';

const copy = {
  fr: {
    step: 'Étape', of: 'sur', skip: 'Passer', back: 'Retour', next: 'Suivant', finish: 'Trouver un terrain', close: 'Fermer le guide',
    legacyLead: 'Vous préférez encore l’ancienne version?', legacyLink: 'Ouvrir CourtClic classique',
    steps: [
      { key: 'welcome', title: 'Bienvenue dans le nouveau CourtClic!', description: 'Le vrai CourtClic, maintenant plus rapide à parcourir.', hint: 'Recherche, résultats et navigation — exactement comme dans l’application.', tags: ['En direct', 'Sans connexion', 'Mobile'], visualLabel: 'Aperçu fidèle de la page de recherche CourtClic' },
      { key: 'search', title: 'Cherchez et ouvrez une fiche', description: 'Voyez la distance, l’adresse et les places libres sur la vraie fiche terrain.', hint: 'Touchez la fiche pour afficher les dates et tous les créneaux.', tags: ['Distance', 'Adresse', 'Créneaux'], visualLabel: 'Fiche terrain CourtClic avec disponibilités' },
      { key: 'courts', title: 'Comparez vos terrains favoris', description: 'Les mêmes dates, heures et cases de disponibilité que dans Mes terrains.', hint: 'L’étoile ajoute un favori; les contrôles permettent de régler ou réordonner la liste.', tags: ['Favoris', 'Dates', 'Votre ordre'], visualLabel: 'Vue fidèle de Mes terrains CourtClic' },
      { key: 'map', title: 'Explorez la vraie vue Carte', description: 'Touchez un marqueur pour ouvrir la fiche du terrain sur la carte.', hint: 'L’adresse complète, les dates et les créneaux restent visibles dans la fiche.', tags: ['Marqueurs', 'Adresse', 'Disponibilités'], visualLabel: 'Vue fidèle de la carte CourtClic et de sa fiche terrain' },
      { key: 'profile', title: 'Votre profil et vos réglages', description: 'Retrouvez vos infos, votre forfait et vos paramètres au même endroit.', hint: 'La fraîcheur des données reste toujours visible dans l’en-tête.', tags: ['Profil', 'Forfait', 'Paramètres'], visualLabel: 'Vue fidèle du profil CourtClic' },
      { key: 'complete', title: 'À vous de jouer!', description: 'Cherchez, ouvrez une fiche, puis choisissez un créneau libre.', hint: 'Vous pouvez revoir ce guide au bas de la page d’accueil.', tags: ['Cherchez', 'Ouvrez', 'Réservez'], visualLabel: 'Parcours CourtClic de la recherche à la réservation' }
    ]
  },
  en: {
    step: 'Step', of: 'of', skip: 'Skip', back: 'Back', next: 'Next', finish: 'Find a court', close: 'Close tour',
    legacyLead: 'Still prefer the previous version?', legacyLink: 'Open classic CourtClic',
    steps: [
      { key: 'welcome', title: 'Welcome to the new CourtClic!', description: 'The real CourtClic interface, now quicker to scan.', hint: 'Search, results and navigation — exactly as they appear in the app.', tags: ['Live', 'No sign-in', 'Mobile'], visualLabel: 'Faithful CourtClic search page preview' },
      { key: 'search', title: 'Search and open a court card', description: 'See distance, address and open slots on the real court card.', hint: 'Tap the card to reveal dates and every available time.', tags: ['Distance', 'Address', 'Time slots'], visualLabel: 'CourtClic court card with availability' },
      { key: 'courts', title: 'Compare your favourite courts', description: 'The same dates, times and availability cells used in My Courts.', hint: 'The star saves a favourite; the controls adjust or reorder your list.', tags: ['Favourites', 'Dates', 'Your order'], visualLabel: 'Faithful CourtClic My Courts preview' },
      { key: 'map', title: 'Explore the real Map view', description: 'Tap a marker to open that court’s card on the map.', hint: 'The full address, dates and open slots stay inside the card.', tags: ['Markers', 'Address', 'Availability'], visualLabel: 'Faithful CourtClic map and court card preview' },
      { key: 'profile', title: 'Your profile and settings', description: 'Find your information, plan and preferences in one place.', hint: 'Data freshness always remains visible in the header.', tags: ['Profile', 'Plan', 'Settings'], visualLabel: 'Faithful CourtClic profile preview' },
      { key: 'complete', title: 'You’re ready to play!', description: 'Search, open a court card, then choose an available time.', hint: 'Replay this guide anytime from the bottom of the home page.', tags: ['Search', 'Open', 'Book'], visualLabel: 'CourtClic journey from search to booking' }
    ]
  }
};

const navItems = [
  { key: 'search', Icon: SearchIcon, fr: 'Recherche', en: 'Search' },
  { key: 'courts', Icon: StarIcon, fr: 'Terrains', en: 'Courts' },
  { key: 'map', Icon: MapIcon, fr: 'Carte', en: 'Map' },
  { key: 'profile', Icon: AccountCircleIcon, fr: 'Profil', en: 'Profile' }
];

const TourDock = ({ active, isFrench }) => (
  <div className="tour-real-dock">
    {navItems.map(({ key, Icon, fr, en }) => (
      <span key={key} className={active === key ? 'is-active' : ''}><Icon /><small>{isFrench ? fr : en}</small></span>
    ))}
  </div>
);

const TourAppHeader = () => (
  <div className="tour-real-header">
    <span className="tour-real-brand"><BrandMark />CourtClic</span>
    <span className="tour-real-fresh"><i />4 min</span>
    <span className="tour-real-avatar"><AccountCircleIcon /></span>
  </div>
);

const TourCourtCard = ({ isFrench, expanded = false, secondary = false }) => {
  const name = secondary ? 'Parc Médéric-Martin' : 'Parc La Fontaine, terrains sportifs';
  return (
    <div className={`tour-real-court${expanded ? ' is-expanded' : ''}`}>
      <div className="tour-real-court-heading">
        <span className="tour-real-star">{secondary ? <StarBorderIcon /> : <StarIcon />}</span>
        <strong>{name}</strong><small>~{secondary ? '0.45' : '2.05'} km</small><ExpandMoreIcon />
      </div>
      <div className="tour-real-court-summary">
        <span>{secondary ? '2329, avenue Gascon, Montréal' : '3933, avenue du Parc-La Fontaine, Montréal'}</span>
        <b>{isFrench ? (secondary ? '15 libres' : '95 libres') : (secondary ? '15 open' : '95 open')}</b>
      </div>
      {expanded && (
        <div className="tour-real-expanded">
          <div className="tour-real-date-tabs"><span className="is-active">{isFrench ? 'JEU. 5 AOÛT' : 'THU AUG 5'}</span><span>{isFrench ? 'VEN. 6 AOÛT' : 'FRI AUG 6'}</span></div>
          <div className="tour-real-grid"><span>16:00</span><i className="is-open">2</i><i /><i className="is-open">1</i><span>19:00</span><i /><i className="is-open">1</i><i /></div>
        </div>
      )}
    </div>
  );
};

const TourAppFrame = ({ active, isFrench, children, className = '' }) => (
  <div className={`tour-real-app ${className}`} aria-hidden="true">
    <TourAppHeader />
    <div className="tour-real-content">{children}</div>
    <TourDock active={active} isFrench={isFrench} />
  </div>
);

const TourVisual = ({ stepKey, isFrench, label }) => {
  if (stepKey === 'welcome') return (
    <div className="product-tour-visual" role="img" aria-label={label}>
      <TourAppFrame active="search" isFrench={isFrench} className="tour-real-home">
        <h3>{isFrench ? 'Trouvez un terrain' : 'Find a court'}</h3>
        <p>{isFrench ? 'Terrains municipaux de Montréal · disponibilités en direct' : 'Montréal municipal courts · live availability'}</p>
        <div className="tour-real-search"><SearchIcon />Ex: La Fontaine</div>
        <TourCourtCard isFrench={isFrench} /><TourCourtCard isFrench={isFrench} secondary />
      </TourAppFrame>
    </div>
  );

  if (stepKey === 'search') return (
    <div className="product-tour-visual" role="img" aria-label={label}>
      <TourAppFrame active="search" isFrench={isFrench} className="tour-real-search-screen">
        <div className="tour-real-search"><SearchIcon />La Fontaine</div>
        <TourCourtCard isFrench={isFrench} expanded />
      </TourAppFrame>
    </div>
  );

  if (stepKey === 'courts') {
    const times = ['12:00', '13:00', '14:00', '15:00'];
    return (
      <div className="product-tour-visual" role="img" aria-label={label}>
        <TourAppFrame active="courts" isFrench={isFrench} className="tour-real-courts-screen">
          <div className="tour-real-section-head"><h3>{isFrench ? 'Mes terrains' : 'My Courts'}</h3><span><EditIcon /><SettingsIcon /><AddIcon /></span></div>
          <div className="tour-real-legend"><b>{isFrench ? 'Terrains libres' : 'Open courts'}</b><i /><small>1</small><i /><small>2-3</small><i /><small>4-5</small></div>
          <div className="tour-real-days"><span className="is-active">MAR.<b>4</b>AOÛT</span><span>MER.<b>5</b>AOÛT</span><span>JEU.<b>6</b>AOÛT</span><span>VEN.<b>7</b>AOÛT</span></div>
          <div className="tour-real-period"><small>‹ {isFrench ? 'Plus tôt' : 'Earlier'}</small><b>{isFrench ? 'Après-midi' : 'Afternoon'}</b><small>{isFrench ? 'Plus tard' : 'Later'} ›</small></div>
          {['La Fontaine, terrains sportifs', 'Médéric-Martin'].map((name, row) => (
            <div className="tour-real-timeline" key={name}>
              <div><DragIndicatorIcon /><StarIcon /><strong>{name}</strong><LocationOnRoundedIcon /></div>
              <div className="tour-real-time-labels">{times.map((time) => <small key={time}>{time}</small>)}</div>
              <div className="tour-real-time-cells"><i /><i className={row ? '' : 'is-open'}>{row ? '' : '2'}</i><i /><i className={row ? 'is-open' : ''}>{row ? '1' : ''}</i></div>
            </div>
          ))}
        </TourAppFrame>
      </div>
    );
  }

  if (stepKey === 'map') return (
    <div className="product-tour-visual" role="img" aria-label={label}>
      <TourAppFrame active="map" isFrench={isFrench} className="tour-real-map-screen">
        <div className="tour-real-map-canvas">
          <span className="tour-real-support">{isFrench ? 'Soutenir CourtClic ♥' : 'Support CourtClic ♥'}</span>
          <LocationOnRoundedIcon className="pin pin-a" /><LocationOnRoundedIcon className="pin pin-b" /><LocationOnRoundedIcon className="pin pin-c" />
          <div className="tour-real-map-popup">
            <strong>Parc La Fontaine, terrains sportifs</strong><small>3933, avenue du Parc-La Fontaine, Montréal</small>
            <div className="tour-real-map-head"><b>{isFrench ? 'DISPONIBILITÉS' : 'AVAILABILITIES'}</b><em>{isFrench ? '4 libres' : '4 open'}</em></div>
            <div className="tour-real-map-dates"><span className="is-active">{isFrench ? 'JEU. 5 AOÛT' : 'THU AUG 5'}</span><span>{isFrench ? 'VEN. 6 AOÛT' : 'FRI AUG 6'}</span></div>
            <div className="tour-real-map-slots"><b>16:00 (2)</b><b>19:00 (1)</b><b>21:00 (1)</b></div>
          </div>
        </div>
      </TourAppFrame>
    </div>
  );

  if (stepKey === 'profile') return (
    <div className="product-tour-visual" role="img" aria-label={label}>
      <TourAppFrame active="profile" isFrench={isFrench} className="tour-real-profile-screen">
        <div className="tour-real-profile-card">
          <div className="tour-real-profile-title"><span><AccountCircleIcon /></span><h3>{isFrench ? 'Profil' : 'Profile'}</h3></div>
          <div className="tour-real-profile-heading"><b>{isFrench ? 'Infos perso' : 'Personal info'}</b><em>{isFrench ? 'Modifier' : 'Edit'}</em></div>
          <div className="tour-real-profile-info"><small>{isFrench ? 'Nom' : 'Name'}</small><strong>{isFrench ? 'Joueur CourtClic' : 'CourtClic player'}</strong><small>{isFrench ? 'Courriel' : 'Email'}</small><strong>user@example.com</strong></div>
          <div className="tour-real-plan"><b>{isFrench ? 'Forfait' : 'Plan'}</b><span>{isFrench ? 'Type' : 'Type'} <em>Premium</em></span><span>{isFrench ? 'Statut' : 'Status'} <i>{isFrench ? 'Actif' : 'Active'}</i></span></div>
          <div className="tour-real-settings"><SettingsIcon /><b>{isFrench ? 'Paramètres' : 'Settings'}</b></div>
        </div>
      </TourAppFrame>
    </div>
  );

  return (
    <div className="product-tour-visual" role="img" aria-label={label}>
      <div className="tour-real-journey" aria-hidden="true">
        <div className="tour-real-journey-step"><small>1</small><div className="tour-real-search"><SearchIcon />La Fontaine</div><b>{isFrench ? 'Cherchez' : 'Search'}</b></div>
        <ArrowForwardRoundedIcon />
        <div className="tour-real-journey-step"><small>2</small><TourCourtCard isFrench={isFrench} /><b>{isFrench ? 'Ouvrez la fiche' : 'Open the card'}</b></div>
        <ArrowForwardRoundedIcon />
        <div className="tour-real-journey-step"><small>3</small><span className="tour-real-book-slot">19:00 (1)</span><b>{isFrench ? 'Réservez' : 'Book'}</b></div>
        <CheckCircleOutlineIcon className="tour-real-ready" />
      </div>
    </div>
  );
};

const ProductTour = ({ open, isFrench, onLanguageChange, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const strings = copy[isFrench ? 'fr' : 'en'];
  const step = strings.steps[activeStep];
  const isLastStep = activeStep === strings.steps.length - 1;

  useEffect(() => { if (open) setActiveStep(0); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" className="product-tour" aria-labelledby="product-tour-title">
      <div className="product-tour-shell">
        <header className="product-tour-header">
          <div className="product-tour-progress-copy">{strings.step} {activeStep + 1} {strings.of} {strings.steps.length}</div>
          <div className="product-tour-header-actions">
            <div className="product-tour-language" aria-label={isFrench ? 'Langue du guide' : 'Tour language'}>
              <button className={!isFrench ? 'is-active' : ''} onClick={() => isFrench && onLanguageChange()}>EN</button>
              <button className={isFrench ? 'is-active' : ''} onClick={() => !isFrench && onLanguageChange()}>FR</button>
            </div>
            <IconButton className="product-tour-close" onClick={onClose} aria-label={strings.close} size="small"><CloseIcon fontSize="small" /></IconButton>
          </div>
        </header>
        <div className="product-tour-progress" aria-hidden="true">{strings.steps.map((item, index) => <span key={item.key} className={index <= activeStep ? 'is-active' : ''} />)}</div>
        <div className="product-tour-body">
          <div className="product-tour-copy"><h2 id="product-tour-title">{step.title}</h2><p className="product-tour-description">{step.description}</p></div>
          <TourVisual stepKey={step.key} isFrench={isFrench} label={step.visualLabel} />
          <div className="product-tour-tags" aria-label={isFrench ? 'Points clés' : 'Key points'}>{step.tags.map((tag) => <span key={tag}><CheckCircleOutlineIcon />{tag}</span>)}</div>
          <div className="product-tour-wayfinding"><p>{step.hint}</p></div>
        </div>
        <footer className="product-tour-footer">
          <div className="product-tour-buttons">
            {activeStep === 0 ? <Button className="product-tour-skip" onClick={onClose}>{strings.skip}</Button> : <Button className="product-tour-back" onClick={() => setActiveStep((current) => current - 1)}>{strings.back}</Button>}
            <Button variant="contained" className="product-tour-next" onClick={() => isLastStep ? onClose() : setActiveStep((current) => current + 1)}>{isLastStep ? strings.finish : strings.next}</Button>
          </div>
          <div className="product-tour-legacy"><span>{strings.legacyLead}</span><a href="https://legacy.courtclic.com">{strings.legacyLink}</a></div>
        </footer>
      </div>
    </Dialog>
  );
};

export default ProductTour;
